import { ActionIcon, Button, NumberInput, Paper, Select, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  useCreateIntegrationKind,
  useDeleteIntegrationKind,
  useIntegrationKinder,
  useUpdateIntegrationKind,
} from '@/api/useIntegrationKinder';
import type { GruppeRow, IntegrationKindPatch, UUID } from '@/api/types';
import { BETREUUNGSMODELL_VALUES, describeApiError } from './utils';

const MONATE = Array.from({ length: 12 }, (_, i) => String(i + 1));
const STUNDEN_DEFAULT = '15';

// Die beiden Teile stehen seit dem Umzug in verschiedenen Reitern:
//
//   Belegung → Themenblock "Inklusion": ``IntegrationKinderTabelle`` —
//     hier werden die Kinder gepflegt (Gruppe, Betreuungsmodell,
//     bewilligte Stunden, Zeitraum).
//   Personalbedarf → "Mindestpersonalbedarf": ``IntegrationKinderZusammenfassung``
//     — zeigt nur, was daraus folgt ("3 Kinder · 43 h").
//
// Die Zusammenfassung ist deshalb reine Anzeige und kein Aufklapp-Knopf
// mehr: Die Liste, die sie früher geöffnet hat, steht nicht mehr in
// demselben Reiter. Ein Knopf, der nichts öffnet, wäre schlimmer als
// keiner.

function useZusammenfassung(planId: UUID) {
  const { t } = useTranslation();
  const kinder = useIntegrationKinder(planId);
  const rows = kinder.data ?? [];
  const summe = rows.reduce((s, k) => s + Number(k.bewilligte_stunden || 0), 0);
  const stundenText = summe.toLocaleString('de-DE', { maximumFractionDigits: 1 });

  // i18next-Pluralformen (`_one`/`_other`) würden am Integritätstest
  // `i18n.test.ts` scheitern, der nur literal im Code stehende Schlüssel
  // findet — `t('...summary', { count })` steht ohne Suffix im Quelltext.
  // Deshalb zwei getrennte Schlüssel, zwischen denen hier (statt i18next)
  // per JS ausgewählt wird.
  return rows.length === 0
    ? t('belegung:section.integration.empty')
    : rows.length === 1
      ? t('belegung:section.integration.summarySingle', { stunden: stundenText })
      : t('belegung:section.integration.summary', {
          count: rows.length,
          stunden: stundenText,
        });
}

interface ZusammenfassungProps {
  planId: UUID;
}

/**
 * Reine Anzeige im Mindestpersonalbedarf: „0 Kinder" bzw. „2 Kinder · 30 h".
 *
 * Gepflegt werden die Kinder im Belegung-Reiter (Themenblock "Inklusion");
 * hier steht nur, was sich daraus für den Personalbedarf ergibt. Der
 * Hinweis darunter sagt, wo man sie ändert — sonst sucht man das
 * Eingabefeld an der Stelle, an der die Zahl steht.
 */
export function IntegrationKinderZusammenfassung({ planId }: ZusammenfassungProps) {
  const { t } = useTranslation();
  const zusammenfassung = useZusammenfassung(planId);

  return (
    <Stack gap={2} style={{ minWidth: 210 }}>
      <Text size="xs" fw={500}>
        {t('belegung:section.integration.label')}
      </Text>
      <Text size="sm" aria-label={t('belegung:section.integration.label')}>
        {zusammenfassung}
      </Text>
      <Text size="xs" c="dimmed">
        {t('belegung:section.integration.pflegeHinweis')}
      </Text>
    </Stack>
  );
}

interface TabelleProps {
  planId: UUID;
  gruppen: ReadonlyArray<GruppeRow>;
  disabled: boolean;
}

/**
 * Die Kinderliste im Belegung-Reiter (Themenblock "Inklusion"): je Kind
 * Gruppe, Betreuungsmodell, bewilligte Fachkraftstunden und Zeitraum.
 *
 * Steht immer offen — sie hat einen eigenen Themenblock, in dem sie nichts
 * verdeckt. Das frühere Auf- und Zuklappen diente nur dazu, die breite
 * Tabelle aus einer engen Kopfzeile herauszuhalten.
 */
export function IntegrationKinderTabelle({ planId, gruppen, disabled }: TabelleProps) {
  const { t } = useTranslation();
  const kinder = useIntegrationKinder(planId);
  const create = useCreateIntegrationKind(planId);
  const update = useUpdateIntegrationKind(planId);
  const remove = useDeleteIntegrationKind(planId);

  const rows = kinder.data ?? [];

  const meldeFehler = (err: unknown) =>
    notifications.show({
      color: 'red',
      title: t('errors:generic'),
      message: describeApiError(err) || t('errors:generic'),
    });

  const patch = (id: UUID, p: IntegrationKindPatch) => {
    update.mutateAsync({ id, patch: p }).catch(meldeFehler);
  };

  return (
    <Paper withBorder p="md">
      <Table withColumnBorders={false} verticalSpacing={2}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('belegung:section.integration.colKind')}</Table.Th>
            <Table.Th>{t('belegung:section.integration.colGruppe')}</Table.Th>
            <Table.Th>{t('belegung:section.integration.colModell')}</Table.Th>
            <Table.Th>{t('belegung:section.integration.colStunden')}</Table.Th>
            <Table.Th>{t('belegung:section.integration.colVon')}</Table.Th>
            <Table.Th>{t('belegung:section.integration.colBis')}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((k, i) => (
            <Table.Tr key={k.id}>
              <Table.Td>
                <Text size="xs">{t('belegung:section.integration.kindLabel', { nr: i + 1 })}</Text>
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  data={gruppen.map((g) => ({ value: g.id, label: g.name }))}
                  value={k.gruppe_id}
                  onChange={(v) => v && patch(k.id, { gruppe_id: v as UUID })}
                  disabled={disabled}
                  allowDeselect={false}
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  data={BETREUUNGSMODELL_VALUES.map((m) => ({
                    value: m,
                    label: t(`belegung:betreuungsmodell.option.${m}`),
                  }))}
                  value={k.betreuungsmodell}
                  onChange={(v) => v && patch(k.id, { betreuungsmodell: v })}
                  disabled={disabled}
                  allowDeselect={false}
                  styles={{ input: { width: 110 } }}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  hideControls
                  min={0}
                  decimalScale={2}
                  placeholder={t('belegung:section.integration.placeholder')}
                  defaultValue={Number(k.bewilligte_stunden)}
                  onBlur={(e) =>
                    patch(k.id, {
                      bewilligte_stunden: e.currentTarget.value || STUNDEN_DEFAULT,
                    })
                  }
                  disabled={disabled}
                  styles={{ input: { textAlign: 'right', width: 70 } }}
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  aria-label={`${t('belegung:section.integration.colVon')} ${t(
                    'belegung:section.integration.kindLabel',
                    { nr: i + 1 },
                  )}`}
                  // "von" darf nie hinter "bis" liegen — sonst entsteht
                  // vorübergehend bis < von, was das Backend mit 422
                  // ablehnt (siehe zeitraum_ungueltig).
                  data={MONATE.filter((m) => Number(m) <= k.aktiv_bis_monat)}
                  value={String(k.aktiv_ab_monat)}
                  onChange={(v) => v && patch(k.id, { aktiv_ab_monat: Number(v) })}
                  disabled={disabled}
                  allowDeselect={false}
                  styles={{ input: { width: 60 } }}
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  aria-label={`${t('belegung:section.integration.colBis')} ${t(
                    'belegung:section.integration.kindLabel',
                    { nr: i + 1 },
                  )}`}
                  // Spiegelbildlich: "bis" darf nie vor "von" liegen.
                  data={MONATE.filter((m) => Number(m) >= k.aktiv_ab_monat)}
                  value={String(k.aktiv_bis_monat)}
                  onChange={(v) => v && patch(k.id, { aktiv_bis_monat: Number(v) })}
                  disabled={disabled}
                  allowDeselect={false}
                  styles={{ input: { width: 60 } }}
                />
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  aria-label={t('belegung:section.integration.remove')}
                  onClick={() => remove.mutateAsync(k.id).catch(meldeFehler)}
                  disabled={disabled}
                >
                  ✕
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Button
        size="xs"
        variant="subtle"
        mt={4}
        disabled={disabled || gruppen.length === 0 || create.isPending}
        onClick={() =>
          create
            .mutateAsync({
              gruppe_id: gruppen[0].id,
              betreuungsmodell: 'ue3_25',
              bewilligte_stunden: STUNDEN_DEFAULT,
            })
            .catch(meldeFehler)
        }
      >
        {t('belegung:section.integration.add')}
      </Button>
    </Paper>
  );
}
